import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const moduleLabel = (k) => (k || "submission").replace(/_/g, " ");

function EmptyState() {
  return (
    <div className="bg-aviana-cream rounded-[2rem] border border-aviana-border px-8 py-16 text-center" data-testid="pending-submissions-empty">
      <Sparkles size={28} className="mx-auto text-aviana-gold mb-4" />
      <p className="font-display text-3xl text-aviana-ink mb-2">You're all caught up.</p>
      <p className="text-aviana-muted text-[15px]">No course submissions are waiting for review — enjoy the quiet.</p>
    </div>
  );
}

function NoteRow({ id, value, onChange }) {
  return (
    <tr className="bg-aviana-surface/40" data-testid={`pending-submission-note-row-${id}`}>
      <td colSpan={4} className="px-6 pb-4 pt-1">
        <textarea rows={3} autoFocus value={value} onChange={(e) => onChange(e.target.value)}
          placeholder="Optional note for the student — included in their “reviewed” email"
          className="w-full bg-white border border-aviana-border rounded-xl px-4 py-3 text-sm text-aviana-ink outline-none focus:border-aviana-plum transition-colors"
          data-testid={`pending-submission-note-${id}`} />
      </td>
    </tr>
  );
}

function SubmissionRow({ s, courseTitle, busy, onReview, noteOpen, onToggleNote, note, onNote }) {
  return (
    <>
    <tr className="border-t border-aviana-border/60 hover:bg-aviana-surface/50 transition-colors" data-testid={`pending-submission-row-${s.id}`}>
      <td className="px-6 py-4">
        <p className="text-aviana-ink font-medium" data-testid={`pending-submission-student-${s.id}`}>{s.user_name || s.user_email || "—"}</p>
        {s.user_name && <p className="text-[12px] text-aviana-muted">{s.user_email}</p>}
      </td>
      <td className="px-6 py-4 text-aviana-inkSoft">
        <p data-testid={`pending-submission-course-${s.id}`}>{courseTitle}</p>
        <p className="text-[11px] tracking-[0.16em] uppercase text-aviana-plum mt-0.5">{moduleLabel(s.module_key)}</p>
      </td>
      <td className="px-6 py-4 text-aviana-inkSoft whitespace-nowrap" data-testid={`pending-submission-date-${s.id}`}>{fmtDate(s.submitted_at)}</td>
      <td className="px-6 py-4 text-right whitespace-nowrap">
        <button onClick={onToggleNote} className="text-[11px] tracking-[0.16em] uppercase text-aviana-muted hover:text-aviana-plum mr-4 transition-colors" data-testid={`pending-submission-add-note-${s.id}`}>
          {noteOpen ? "Hide note" : note.trim() ? "Edit note" : "Add note"}
        </button>
        <Link to="/admin/lms/submissions" className="inline-flex items-center gap-1 text-[11px] tracking-[0.16em] uppercase text-aviana-muted hover:text-aviana-plum mr-4 transition-colors" data-testid={`pending-submission-open-${s.id}`}>
          Open <ExternalLink size={12} />
        </Link>
        <button onClick={() => onReview(s)} disabled={busy} className="btn-gold !py-2 !px-4 !text-[10px] inline-flex items-center gap-1.5" data-testid={`pending-submission-review-${s.id}`}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Mark as Reviewed
        </button>
      </td>
    </tr>
    {noteOpen && <NoteRow id={s.id} value={note} onChange={onNote} />}
    </>
  );
}

export const PendingSubmissions = () => {
  const [items, setItems] = useState(null);
  const [courses, setCourses] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [notes, setNotes] = useState({});
  const [openNotes, setOpenNotes] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [subs, cs] = await Promise.all([
          api.get("/admin/lms/submissions?status=submitted"),
          api.get("/admin/lms/courses"),
        ]);
        setItems(subs.data);
        setCourses(Object.fromEntries((cs.data || []).map((c) => [String(c.wp_post_id), c.title])));
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not load submissions");
        setItems([]);
      }
    })();
  }, []);

  const review = async (s) => {
    setBusyId(s.id);
    try {
      await api.post(`/admin/lms/submissions/${s.id}/review`, { status: "approved", reviewer_notes: (notes[s.id] || "").trim() });
      setItems((list) => list.filter((x) => x.id !== s.id));
      toast.success(`Marked reviewed · ${s.user_name || "student"} emailed`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not mark as reviewed");
    }
    setBusyId(null);
  };

  if (items === null) return <div className="py-12 text-aviana-muted"><Loader2 className="animate-spin inline mr-2" size={16} />Loading…</div>;
  if (items.length === 0) return <EmptyState />;
  return (
    <div className="bg-aviana-cream rounded-[2rem] border border-aviana-border overflow-x-auto" data-testid="pending-submissions">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left">
            {["Student", "Course", "Submitted", ""].map((h) => <th key={h} className="overline-plum px-6 py-4 font-medium">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {items.map((s) => (
            <SubmissionRow key={s.id} s={s} courseTitle={courses[String(s.course_wp_id)] || `Course ${s.course_wp_id}`}
              busy={busyId === s.id} onReview={review}
              noteOpen={!!openNotes[s.id]} onToggleNote={() => setOpenNotes((o) => ({ ...o, [s.id]: !o[s.id] }))}
              note={notes[s.id] || ""} onNote={(v) => setNotes((n) => ({ ...n, [s.id]: v }))} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PendingSubmissions;
